import { useCallback } from 'react';
import { createBlockId, isBlockTypeEnabled, newBlock } from '../blocks/blockLibrary';
import type { BlockPatch, BlockType, EmailBlock } from '../HtmlEditor.types';

type UseBlockEditorArgs = {
  blocks: EmailBlock[];
  snapshot: (next: EmailBlock[]) => void;
  onSelect?: (id: string | null) => void;
};

export function useBlockEditor({ blocks, snapshot, onSelect }: UseBlockEditorArgs) {
  const updateBlock = useCallback(
    (id: string, patch: BlockPatch) => {
      snapshot(blocks.map((b) => (b.id === id ? ({ ...b, ...patch } as EmailBlock) : b)));
    },
    [blocks, snapshot]
  );

  const deleteBlock = useCallback(
    (id: string) => {
      snapshot(blocks.filter((b) => b.id !== id));
      onSelect?.(null);
    },
    [blocks, snapshot, onSelect]
  );

  const duplicateBlock = useCallback(
    (id: string) => {
      const i = blocks.findIndex((b) => b.id === id);
      if (i < 0) return;
      const copy = { ...blocks[i], id: createBlockId() } as EmailBlock;
      snapshot([...blocks.slice(0, i + 1), copy, ...blocks.slice(i + 1)]);
    },
    [blocks, snapshot]
  );

  const moveBlock = useCallback(
    (id: string, dir: number) => {
      const i = blocks.findIndex((b) => b.id === id);
      const j = i + dir;
      if (j < 0 || j >= blocks.length) return;
      const next = [...blocks];
      [next[i], next[j]] = [next[j], next[i]];
      snapshot(next);
    },
    [blocks, snapshot]
  );

  const insertAt = useCallback(
    (idx: number, type: BlockType) => {
      if (!isBlockTypeEnabled(type)) return;
      const b = newBlock(type);
      snapshot([...blocks.slice(0, idx), b, ...blocks.slice(idx)]);
      onSelect?.(b.id);
    },
    [blocks, snapshot, onSelect]
  );

  return {
    updateBlock,
    deleteBlock,
    duplicateBlock,
    moveBlock,
    insertAt,
  };
}
